import { Field, Label, Radio, RadioGroup, Switch } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/20/solid"; // ou votre icône de coche
import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CartLoaderOverlay from "../components/CartLoaderOverlay";
import { RootState } from "../store";
import { addPosterToCart } from "../store/cartSlice";
import {
  MAP_STYLE_OPTIONS,
  MapStyleOption,
  setMapStyle,
  setShowLabels,
  setShowTerrain,
} from "../store/mapSlice";
import { setTraceColor } from "../store/traceSlice"; // Import from traceSlice
import { HiShoppingCart } from "react-icons/hi";
import { useAddToCart } from '../utils/useAddToCart';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

interface MapStyleProps {
  editorPreviewRef: React.RefObject<any>;
}

const MapStyle: React.FC<MapStyleProps> = ({ editorPreviewRef }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { selectedStyleId, showTerrain, showLabels } = useSelector(
    (state: RootState) => state.map
  );
  const selectedStyleInfo = MAP_STYLE_OPTIONS.find(
    (s) => s.id === selectedStyleId
  );
  const navigate = useNavigate();
  const labels = useSelector((state: RootState) => state.labels);
  const points = useSelector((state: RootState) => state.points.points);
  const layout = useSelector((state: RootState) => state.layout);
  const map = useSelector((state: RootState) => state.map);
  const trace = useSelector((state: RootState) => state.trace);
  const profile = useSelector((state: RootState) => state.profile);
  const product = useSelector((state: RootState) => state.product);
  const activities = useSelector(
    (state: RootState) => state.activities.activities
  );
  const activeActivityIds = useSelector(
    (state: RootState) => state.activities.activeActivityIds
  );

  const { handleAddToCart, isAddingToCart, isGeneratingPreview, uploadProgress, cartOverlay } = useAddToCart(editorPreviewRef);

  const handleTerrainToggle = (enabled: boolean) => {
    // Assurez-vous que le style actuel supporte le terrain avant d'activer
    if (selectedStyleInfo?.hasTerrain) {
      dispatch(setShowTerrain(enabled));
    } else if (!enabled) {
      dispatch(setShowTerrain(false)); // Permettre de désactiver même si non supporté
    }
  };

  const handleMapStyleChange = (id: string) => {
    dispatch(setMapStyle(id));
    const selectedStyle = MAP_STYLE_OPTIONS.find((style) => style.id === id);
    if (selectedStyle && selectedStyle.defaultTraceColor) {
      dispatch(setTraceColor(selectedStyle.defaultTraceColor)); // Ensure defaultTraceColor is applied
    }
  };

  return (
    <div className="w-full w-[700px] max-w-[700px] mx-auto px-4 space-y-6 text-white">
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-lg font-semibold font-sans">
          {t("mapStylePage.mapStyles")}
        </h1>
        <p className="text-gray-400 font-light text-sm">
          {t("mapStylePage.subtitle")}
        </p>
      </div>

      {/* Map Style Selection */}
      <Field>
        <Label className="text-sm font-medium">
          {t("mapStylePage.mapStyles")}
        </Label>
        <RadioGroup
          value={selectedStyleId}
          onChange={handleMapStyleChange} // Use the updated handler
          className="space-y-2 mt-3"
        >
          <div className="grid grid-cols-3 gap-2">
            {MAP_STYLE_OPTIONS.map((style: MapStyleOption) => {
              // Extract username and style_id from the mapbox URL
              const urlParts = style.url
                .replace("mapbox://styles/", "")
                .split("/");
              const username = urlParts[0];
              const style_id = urlParts[1];

              // Construct the Mapbox Static Image API URL
              const thumbnailUrl = `https://api.mapbox.com/styles/v1/${username}/${style_id}/static/2.35,48.85,5,0,0/200x200?access_token=${MAPBOX_TOKEN}`;

              return (
                <Radio
                  key={style.id}
                  value={style.id}
                  className={({ checked }) =>
                    clsx(
                      "relative flex cursor-pointer rounded-lg p-1 focus:outline-none",
                      checked
                        ? "ring-2 ring-offset-2 ring-offset-gray-900 ring-blue-500"
                        : "ring-1 ring-white/10 hover:ring-white/30"
                    )
                  }
                >
                  {({ checked }) => (
                    <>
                      <img
                        src={thumbnailUrl}
                        alt={style.name}
                        className="w-full h-auto object-cover rounded bg-gray-600"
                      />
                      {checked && (
                        <div className="absolute inset-0 rounded-lg ring-2 ring-inset ring-blue-500 flex items-center justify-center bg-black/30">
                          <CheckIcon className="h-6 w-6 text-white" />
                        </div>
                      )}
                    </>
                  )}
                </Radio>
              );
            })}
          </div>
        </RadioGroup>
      </Field>

      {/* Toggles */}
      <div className="space-y-4 pt-4">
        {/* Terrain Toggle */}
        <Field className="flex items-center justify-between">
          <Label
            className="text-sm font-medium cursor-pointer"
            passive
          >
            {t("mapStylePage.terrain3D")}
            {!selectedStyleInfo?.hasTerrain && (
              <span className="text-xs text-gray-500 ml-1">
                ({t("mapStylePage.terrainNotSupported")})
              </span>
            )}
          </Label>
          <Switch
            checked={showTerrain && !!selectedStyleInfo?.hasTerrain}
            onChange={handleTerrainToggle}
            disabled={!selectedStyleInfo?.hasTerrain}
            className={clsx(
              "group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900",
              "data-[checked]:bg-blue-600 bg-gray-500",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            <span
              aria-hidden="true"
              className={clsx(
                "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                "translate-x-0 data-[checked]:translate-x-5"
              )}
            />
          </Switch>
        </Field>

        {/* Labels Toggle */}
        <Field className="flex items-center justify-between">
          <Label
            className="text-sm font-medium cursor-pointer"
            passive
          >
            {t("mapStylePage.showMapLabels")}
          </Label>
          <Switch
            checked={showLabels}
            onChange={(val) => dispatch(setShowLabels(val))}
            className={clsx(
              "group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900",
              "data-[checked]:bg-blue-600 bg-gray-500"
            )}
          >
            <span
              aria-hidden="true"
              className={clsx(
                "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                "translate-x-0 data-[checked]:translate-x-5"
              )}
            />
          </Switch>
        </Field>
      </div>

      {/* Bouton Ajouter au panier en bas, style identique à Overview */}
      {cartOverlay}
      <button
        onClick={handleAddToCart}
        disabled={isAddingToCart}
        className="w-full text-sm cursor-pointer flex justify-center items-center space-x-2 text-white py-2 rounded-sm disabled:opacity-50 disabled:cursor-wait mt-8"
        style={{ backgroundColor: isAddingToCart ? '#FC4C02' : '#FC4C02', opacity: isAddingToCart ? 0.5 : 1 }}
        onMouseOver={e => (e.currentTarget.style.backgroundColor = '#d63a00')}
        onMouseOut={e => (e.currentTarget.style.backgroundColor = '#FC4C02')}
      >
        <HiShoppingCart className="w-4 h-4" />
        <span>{t('common.addToCart', 'Ajouter au panier')}</span>
      </button>
    </div>
  );
};

export default MapStyle;
